import { AnyExtension, Extension, mergeAttributes, Node } from '@tiptap/core';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import History from '@tiptap/extension-history';
import Italic from '@tiptap/extension-italic';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';

import type { ProposalDeclarationFieldSituacao } from 'services/actions/ai_management_proposal_documents';

// Schema FECHADO: só o que o ProposalHtmlSanitizer do back aceita (p, br, strong, em, u, ul, ol,
// li, span[data-campo] e text-align em p/li). Colar texto de fora descarta o resto.

export type TextAlign = 'left' | 'center' | 'right' | 'justify';
const ALINHAMENTOS: TextAlign[] = ['left', 'center', 'right', 'justify'];

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    alinhamento: {
      setTextAlign: (align: TextAlign) => ReturnType;
    };
  }
}

const AlinhamentoDoTexto = Extension.create({
  name: 'alinhamento',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'listItem'],
        attributes: {
          textAlign: {
            default: null,
            parseHTML: (element) => {
              const valor = element.style.textAlign as TextAlign;
              return ALINHAMENTOS.includes(valor) ? valor : null;
            },
            renderHTML: (attributes) =>
              attributes.textAlign ? { style: `text-align: ${attributes.textAlign}` } : {},
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setTextAlign:
        (align) =>
        ({ commands }) =>
          ['paragraph', 'listItem']
            .map((tipo) => commands.updateAttributes(tipo, { textAlign: align }))
            .some(Boolean),
    };
  },
});

export interface CampoAttrs {
  id: string | null;
  valor: string;
  rotulo: string;
  sugerido: string | null;
  situacao: ProposalDeclarationFieldSituacao;
}

export const placeholderDoCampo = (rotulo: string) => `[Inserir ${rotulo}]`;

const textoDoCampo = (attrs: CampoAttrs) =>
  attrs.valor.trim() ? attrs.valor : placeholderDoCampo(attrs.rotulo);

// Campo a preencher: no HTML só `data-campo` e o texto (valor ou "[Inserir <rótulo>]"); rótulo,
// sugerido e situação ficam fora do HTML (o back guarda a situação no array `campos`).
const Campo = Node.create({
  name: 'campo',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-campo'),
        renderHTML: (attributes) => ({ 'data-campo': attributes.id }),
      },
      valor: { default: '', parseHTML: (element) => element.textContent ?? '', rendered: false },
      rotulo: { default: 'dado', rendered: false },
      sugerido: { default: null, rendered: false },
      situacao: { default: 'pendente', rendered: false },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-campo]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), textoDoCampo(node.attrs as CampoAttrs)];
  },

  renderText({ node }) {
    return textoDoCampo(node.attrs as CampoAttrs);
  },

  addNodeView() {
    return ({ node: inicial, editor, getPos }) => {
      let node = inicial;
      let editando = false;
      const dom = document.createElement('span');

      // O mesmo campo pode aparecer mais de uma vez (o responsável na qualificação e na
      // assinatura): aceitar, recusar ou digitar vale para todos.
      const atualizar = (attrs: Partial<CampoAttrs>) => {
        const pos = typeof getPos === 'function' ? getPos() : undefined;
        if (typeof pos !== 'number') return;
        const { tr } = editor.view.state;
        const { id } = node.attrs;
        editor.view.state.doc.descendants((outro, outroPos) => {
          if (outro.type.name === 'campo' && id && outro.attrs.id === id) {
            tr.setNodeMarkup(outroPos, undefined, { ...outro.attrs, ...attrs });
          }
        });
        if (!id) tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs });
        editor.view.dispatch(tr);
      };

      const botao = (rotulo: string, titulo: string, acao: () => void) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'campo-acao';
        el.dataset.act = rotulo === '✓' ? 'ok' : 'no';
        const icone = document.createElement('span');
        icone.className = 'material-icons-outlined';
        icone.textContent = rotulo === '✓' ? 'check' : 'close';
        el.appendChild(icone);
        el.title = titulo;
        el.setAttribute('aria-label', titulo);
        el.addEventListener('mousedown', (event) => event.preventDefault());
        el.addEventListener('click', (event) => {
          event.preventDefault();
          acao();
        });
        return el;
      };

      const desenhar = () => {
        const attrs = node.attrs as CampoAttrs;
        dom.className = `campo campo-${attrs.situacao}`;
        dom.replaceChildren();

        if (editando) {
          const input = document.createElement('input');
          input.className = 'campo-input';
          input.value = attrs.valor;
          input.placeholder = attrs.rotulo;
          const gravar = () => {
            if (!editando) return;
            editando = false;
            const valor = input.value.trim();
            if (!valor) {
              desenhar();
              return;
            }
            atualizar({
              valor,
              situacao: attrs.situacao === 'pendente' ? 'aceito' : attrs.situacao,
            });
          };
          input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              gravar();
            } else if (event.key === 'Escape') {
              editando = false;
              desenhar();
            }
          });
          input.addEventListener('blur', gravar);
          dom.appendChild(input);
          setTimeout(() => input.focus(), 0);
          return;
        }

        const valor = document.createElement('span');
        valor.className = 'campo-valor';
        valor.textContent = textoDoCampo(attrs);
        dom.appendChild(valor);
        if (!editor.isEditable) return;

        const digitavel = attrs.situacao === 'recusado' || attrs.situacao === 'pendente';
        if (digitavel) {
          valor.title = 'Clique e digite';
          valor.addEventListener('mousedown', (event) => {
            event.preventDefault();
            editando = true;
            desenhar();
          });
        }
        if (attrs.situacao === 'sugerido' || (digitavel && attrs.sugerido)) {
          dom.appendChild(
            botao('✓', `Aceitar ${attrs.rotulo}`, () =>
              atualizar({ valor: attrs.sugerido ?? attrs.valor, situacao: 'aceito' }),
            ),
          );
        }
        if (attrs.situacao === 'sugerido' || attrs.situacao === 'aceito') {
          dom.appendChild(
            botao('✗', `Recusar ${attrs.rotulo}`, () =>
              atualizar({ valor: '', situacao: 'recusado' }),
            ),
          );
        }
      };

      desenhar();

      return {
        dom,
        update: (novo) => {
          if (novo.type !== node.type) return false;
          node = novo;
          if (!editando) desenhar();
          return true;
        },
        stopEvent: (event) => (event.target as HTMLElement)?.tagName === 'INPUT',
        ignoreMutation: () => true,
      };
    };
  },
});

export const extensoesDaDeclaracao = (): AnyExtension[] => [
  Document,
  Paragraph,
  Text,
  HardBreak,
  Bold,
  Italic,
  Underline,
  BulletList,
  OrderedList,
  ListItem,
  History,
  AlinhamentoDoTexto,
  Campo,
];
